import { VARIABLE_DATA_ATTR, isKnownStaticVariableTag } from "@simplyk/common";
import {
  $applyNodeReplacement,
  $createTextNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  Spread,
  TextNode,
} from "lexical";

export type SerializedVariableNode = Spread<{ tag: string }, SerializedTextNode>;

/**
 * VariableNode - an inline, atomic, styleable token for email variables
 * (firstname / lastname / last_amount). Implemented as a token-mode TextNode
 * which takes all formatting and styling from the parent node except for font-size.
 */
export class VariableNode extends TextNode {
  __tag: string;

  static getType(): string {
    return "variable";
  }

  static clone(node: VariableNode): VariableNode {
    return new VariableNode(node.__tag, node.__text, node.__key);
  }

  constructor(tag: string, text: string, key?: NodeKey) {
    super(text, key);
    this.__tag = tag;
  }

  getTag(): string {
    return this.getLatest().__tag;
  }

  setTag(tag: string): void {
    this.getWritable().__tag = tag;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.classList.add("styled-tag-chip");
    dom.setAttribute(VARIABLE_DATA_ATTR, this.__tag);
    return dom;
  }

  updateDOM(prevNode: this, dom: HTMLElement, config: EditorConfig): boolean {
    const recreate = super.updateDOM(prevNode, dom, config);
    if (recreate) {
      return true;
    }
    dom.classList.add("styled-tag-chip");
    dom.setAttribute(VARIABLE_DATA_ATTR, this.__tag);
    return false;
  }

  exportDOM(): DOMExportOutput {
    const span = document.createElement("span");
    span.className = "styled-tag-chip";
    span.setAttribute(VARIABLE_DATA_ATTR, this.__tag);
    span.setAttribute("contenteditable", "false");
    span.textContent = this.__text;

    // `fr-original-style` is lifted onto the substituted value by `replaceBodyVariables`;
    // `style` mirrors it so the deselected-block static-HTML preview actually renders it.
    const { color } = parseStyleColor(this.getStyle());
    const variableStyling: string[] = [];
    if (color) {
      variableStyling.push(`color: ${color}`);
    }
    if (variableStyling.length > 0) {
      const styleValue = variableStyling.join("; ");
      span.setAttribute("fr-original-style", styleValue);
      span.setAttribute("style", styleValue);
    }

    let element: HTMLElement = span;
    if (this.hasFormat("underline")) {
      element = wrapWith("u", element);
    }
    if (this.hasFormat("italic")) {
      element = wrapWith("em", element);
    }
    if (this.hasFormat("bold")) {
      element = wrapWith("strong", element);
    }

    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (node: Node) => {
        const span = node as HTMLSpanElement;
        if (!span.hasAttribute(VARIABLE_DATA_ATTR)) {
          return null;
        }
        const tag = span.getAttribute(VARIABLE_DATA_ATTR) ?? "";
        if (!isKnownStaticVariableTag(tag)) {
          return {
            conversion: convertUnsupportedVariableElement,
            priority: 1,
          };
        }
        return {
          conversion: convertVariableElement,
          priority: 1,
        };
      },
    };
  }

  static importJSON(serializedNode: SerializedVariableNode): VariableNode {
    const node = $createVariableNode(serializedNode.tag, serializedNode.text);
    node.setFormat(serializedNode.format);
    node.setStyle(serializedNode.style);
    node.setDetail(serializedNode.detail);
    node.setMode(serializedNode.mode);
    return node;
  }

  exportJSON(): SerializedVariableNode {
    return {
      ...super.exportJSON(),
      tag: this.__tag,
      type: "variable",
      version: 1,
    };
  }
}

const wrapWith = (tagName: string, child: HTMLElement): HTMLElement => {
  const wrapper = document.createElement(tagName);
  wrapper.appendChild(child);
  return wrapper;
};

const parseStyleColor = (style: string | null): { color?: string } => {
  if (!style) {
    return {};
  }
  const result: { color?: string } = {};
  // Require a non-whitespace value so an empty declaration (`color:` left behind
  // after clearing) reads as "no color" instead of an empty string.
  const colorMatch = style.match(/(?:^|;\s*)color:\s*([^;\s][^;]*)/i);
  if (colorMatch) {
    result.color = colorMatch[1].trim();
  }
  return result;
};

// Only color is read from ancestors. Font-size is intentionally left to inherit from the parent
// (see the class doc) and bold/italic/underline are applied natively by Lexical's
// <strong>/<em>/<u> forChild conversions now that this is a TextNode - re-applying them here
// would cancel the native toggle out.
const detectStyleFromAncestors = (domNode: Node): { color?: string } => {
  const result: { color?: string } = {};
  let current = domNode.parentElement;
  while (current) {
    const inlineStyle = current.getAttribute && current.getAttribute("style");
    if (inlineStyle) {
      const parsed = parseStyleColor(inlineStyle);
      if (!result.color && parsed.color) {
        result.color = parsed.color;
      }
    }
    current = current.parentElement;
  }
  return result;
};

function convertVariableElement(domNode: Node): DOMConversionOutput {
  const span = domNode as HTMLSpanElement;
  const tag = span.getAttribute(VARIABLE_DATA_ATTR) || "";
  const text = span.textContent || span.innerText || "";

  const ancestorStyle = detectStyleFromAncestors(domNode);
  const chipStyle = parseStyleColor(span.getAttribute("fr-original-style"));
  // Styles stored on the chip itself win over ancestor-inherited styles.
  const color = chipStyle.color ?? ancestorStyle.color;

  const node = $createVariableNode(tag, text);
  const styleParts: string[] = [];
  if (color) {
    styleParts.push(`color: ${color}`);
  }
  if (styleParts.length > 0) {
    node.setStyle(styleParts.join("; "));
  }
  return { node };
}

// Unsupported (legacy) variables are downgraded to plain bracketed text so they
// read as a manual placeholder rather than a working variable chip.
function convertUnsupportedVariableElement(domNode: Node): DOMConversionOutput {
  const span = domNode as HTMLSpanElement;
  const text = span.textContent || span.innerText || "";
  return { node: $createTextNode(`[${text}]`) };
}

export function $createVariableNode(tag: string, text: string): VariableNode {
  const node = new VariableNode(tag, text);
  node.setMode("token");
  return $applyNodeReplacement(node);
}

export function $isVariableNode(node: LexicalNode | null | undefined): node is VariableNode {
  return node instanceof VariableNode;
}
